import { zodResolver } from '@hookform/resolvers/zod';
import { useMainCategories } from 'features/main-category/hooks/useMainCategories';
import { Camera, ListPlus, Loader, Trash2, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { z } from 'zod';
import { useAddSubCategory } from '../hooks/useAddSubCategory';
import { subCategoryServices } from '../services/sub-category.service';

const subCategorySchema = z.object({
  name: z.object({
    english: z.string().min(1, 'English name is required'),
    hindi: z.string().optional(),
    marwari: z.string().optional(),
  }),
  description: z
  .object({
    english: z
      .string()
      .min(250, 'English description must be at least 250 characters')
      .max(500, 'English description must be at most 500 characters'),

    hindi: z
      .string()
      .optional()
      .refine((val) => {
        if (!val || val.trim() === '') return true;
        return val.length >= 250 && val.length <= 500;
      }, {
        message: 'Hindi description must be between 250 and 500 characters',
      }),

    marwari: z
      .string()
      .optional()
      .refine((val) => {
        if (!val || val.trim() === '') return true;
        return val.length >= 250 && val.length <= 500;
      }, {
        message: 'Marwari description must be between 250 and 500 characters',
      }),
  })
  .optional(),
  parentCategory: z.string().min(1, 'Parent category is required'),
  icon: z.string().optional().or(z.literal('')),
  image: z.string().optional().or(z.literal('')),
});


const AddSubCategoryModal = (props) => {
  const { isAddModalOpen, setIsAddModalOpen } = props;
  const { data: mainCategories } = useMainCategories({ limit: 99, isActive: true });
  const { mutate: addSubCategory, isPending } = useAddSubCategory();

  // Icon upload state
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [isUploadingIcon, setIsUploadingIcon] = useState(false);
  const iconInputRef = useRef(null);

  // Image upload state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(subCategorySchema),
    mode: 'onChange',
  });

  const validateFile = (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return false;
    }
    return true;
  };

  const handleIconSelect = (file) => {
    if (!file || !validateFile(file)) return;
    setIconFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setIconPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveIcon = () => {
    setIconFile(null);
    setIconPreview(null);
    if (iconInputRef.current) iconInputRef.current.value = '';
  };

  const handleImageSelect = (file) => {
    if (!file || !validateFile(file)) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleModalClose = () => {
    reset();
    handleRemoveIcon();
    handleRemoveImage();
    setIsAddModalOpen(false);
  };

  const onSubmit = async (data) => {
    try {
      let iconKey = '';
      let imageKey = '';

      // Upload icon if selected
      if (iconFile) {
        setIsUploadingIcon(true);
        const uploadResponse = await subCategoryServices.uploadFile(iconFile);
        iconKey = uploadResponse?.data?.key || '';
        setIsUploadingIcon(false);
      }

      // Upload image if selected
      if (imageFile) {
        setIsUploadingImage(true);
        const uploadResponse = await subCategoryServices.uploadFile(imageFile);
        imageKey = uploadResponse?.data?.key || '';
        setIsUploadingImage(false);
      }

      const formattedData = {
        ...data,
        icon: iconKey || data.icon || '',
        image: imageKey || data.image || '',
      };

      addSubCategory(formattedData, {
        onSuccess: () => {
          reset();
          handleRemoveIcon();
          handleRemoveImage();
          setIsAddModalOpen(false);
        },
      });
    } catch (error) {
      setIsUploadingIcon(false);
      setIsUploadingImage(false);
      toast.error(error?.response?.data?.message || 'Failed to upload file');
    }
  };

  if (!isAddModalOpen) return null;

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar'>
        {/* Header */}
        <div className='sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between'>
          <h2 className='text-lg font-semibold'>Add New Sub-Category</h2>
          <button onClick={handleModalClose} className='p-1 hover:bg-muted rounded-md transition-colors'>
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit, (err) => console.log('ERRORS:', err))} className='p-6 space-y-6'>
          {/* Parent Category Selection */}
          <div>
            <label className='block text-sm font-medium mb-1'>Parent Category *</label>
            <select {...register('parentCategory')} className='w-full px-3 py-2 text-sm'>
              <option value=''>Select Parent Category</option>
              {mainCategories?.data?.map((parent) => (
                <option key={parent._id} value={parent._id}>
                  {parent.name?.english}
                </option>
              ))}
            </select>
            {errors.parentCategory && <p className='text-destructive text-xs mt-1'>{errors.parentCategory.message}</p>}
          </div>

          {/* Name Section */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold text-foreground'>Name (Multilingual)</h3>

            <div>
              <label className='block text-sm font-medium mb-1'>English Name *</label>
              <input
                type='text'
                {...register('name.english')}
                className='w-full px-3 py-2 text-sm'
                placeholder='e.g., Plumbing Services'
              />
              {errors.name?.english && <p className='text-destructive text-xs mt-1'>{errors.name.english.message}</p>}
            </div>

            <div>
              <label className='block text-sm font-medium mb-1'>Hindi Name</label>
              <input
                type='text'
                {...register('name.hindi')}
                className='w-full px-3 py-2 text-sm'
                placeholder='e.g., प्लंबिंग सेवाएं'
              />
            </div>

            <div>
              <label className='block text-sm font-medium mb-1'>Marwari Name</label>
              <input
                type='text'
                {...register('name.marwari')}
                className='w-full px-3 py-2 text-sm'
                placeholder='e.g., प्लंबिंग सेवाएं'
              />
            </div>
          </div>

          {/* Description Section */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold text-foreground'>Description (Multilingual)</h3>

            <div>
              <label className='block text-sm font-medium mb-1'>English Description *</label>
              <textarea
                {...register('description.english')}
                rows={3}
                className='w-full px-3 py-2 text-sm'
                placeholder='Professional plumbing repair and installation'
              />
              {errors.description?.english && (
                <p className='text-destructive text-xs mt-1'>{errors.description.english.message}</p>
              )}
            </div>

            <div>
              <label className='block text-sm font-medium mb-1'>Hindi Description</label>
              <textarea {...register('description.hindi')} rows={3} className='w-full px-3 py-2 text-sm' />
            </div>

            <div>
              <label className='block text-sm font-medium mb-1'>Marwari Description</label>
              <textarea {...register('description.marwari')} rows={3} className='w-full px-3 py-2 text-sm' />
            </div>
          </div>

          {/* Media & Display */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold text-foreground'>Media & Display</h3>

            {/* Icon Upload */}
            {/* <div>
              <label className='block text-sm font-medium mb-2'>Icon</label>
              {iconPreview ? (
                <div className='flex items-center space-x-4'>
                  <img src={iconPreview} alt='Icon preview' className='w-16 h-16 rounded-lg object-cover border border-border' />
                  <div className='flex flex-col space-y-2'>
                    <p className='text-xs text-muted-foreground'>{iconFile?.name}</p>
                    <button
                      type='button'
                      onClick={handleRemoveIcon}
                      className='flex items-center space-x-1 text-xs text-destructive hover:text-destructive/80'
                    >
                      <Trash2 size={14} />
                      <span>Remove</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => iconInputRef.current?.click()}
                  className='border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors'
                >
                  <input
                    ref={iconInputRef}
                    type='file'
                    accept='image/*'
                    onChange={(e) => handleIconSelect(e.target.files?.[0])}
                    className='hidden'
                  />
                  <div className='flex flex-col items-center space-y-2'>
                    <div className='w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center'>
                      <Camera size={20} className='text-primary' />
                    </div>
                    <p className='text-sm text-muted-foreground'>Click to upload icon</p>
                    <p className='text-xs text-muted-foreground'>PNG, JPG up to 5MB</p>
                  </div>
                </div>
              )}
              {errors.icon && <p className='text-destructive text-xs mt-1'>{errors.icon.message}</p>}
            </div> */}

            {/* Image Upload */}
            <div>
              <label className='block text-sm font-medium mb-2'>Image</label>
              {imagePreview ? (
                <div className='flex items-center space-x-4'>
                  <img src={imagePreview} alt='Image preview' className='w-24 h-24 rounded-lg object-cover border border-border' />
                  <div className='flex flex-col space-y-2'>
                    <p className='text-xs text-muted-foreground'>{imageFile?.name}</p>
                    <button
                      type='button'
                      onClick={handleRemoveImage}
                      className='flex items-center space-x-1 text-xs text-destructive hover:text-destructive/80'
                    >
                      <Trash2 size={14} />
                      <span>Remove</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => imageInputRef.current?.click()}
                  className='border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors'
                >
                  <input
                    ref={imageInputRef}
                    type='file'
                    accept='image/*'
                    onChange={(e) => handleImageSelect(e.target.files?.[0])}
                    className='hidden'
                  />
                  <div className='flex flex-col items-center space-y-2'>
                    <div className='w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center'>
                      <Camera size={20} className='text-primary' />
                    </div>
                    <p className='text-sm text-muted-foreground'>Click to upload image</p>
                    <p className='text-xs text-muted-foreground'>PNG, JPG up to 5MB</p>
                  </div>
                </div>
              )}
              {errors.image && <p className='text-destructive text-xs mt-1'>{errors.image.message}</p>}
            </div>
          </div>

          {/* Actions */}
          <div className='flex justify-end space-x-3 pt-4 border-t border-border'>
            <button
              type='button'
              onClick={handleModalClose}
              className='px-4 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={isSubmitting || isPending || isUploadingIcon || isUploadingImage}
              className='px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50'
            >
              {isPending || isUploadingIcon || isUploadingImage ? (
                <div className='flex items-center justify-center'>
                  <Loader size={16} className='animate-spin mr-2' />
                  {isUploadingIcon || isUploadingImage ? 'Uploading...' : 'Creating...'}
                </div>
              ) : (
                <div className='flex items-center justify-center'>
                  <ListPlus size={16} className='mr-2' />
                  Create Sub-Category
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSubCategoryModal;
